'use client';

import { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';
import TemplateCard from './TemplateCard';
import FilterSidebar from './FilterSidebar';
import TemplateDetailsModal from './TemplateDetailsModal';

interface Template {
  id: string;
  title: string;
  description: string;
  category: string;
  rating: number;
  totalRatings: number;
  downloads: number;
  author: string;
  authorRole: string;
  thumbnail: string;
  thumbnailAlt: string;
  department: string;
  lastUpdated: string;
  tags: string[];
  longDescription: string;
  features: string[];
  requirements: string[];
  version: string;
  compatibility: string[];
}

const TemplateGalleryInteractive = () => {
  const router = useRouter();
  const [isHydrated, setIsHydrated] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<'popular' | 'recent' | 'rating' | 'downloads'>('popular');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null);
  const [filters, setFilters] = useState({
    categories: [] as string[],
    departments: [] as string[],
    authors: [] as string[],
    minRating: 0,
    minDownloads: 0
  });

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const mockTemplates: Template[] = [
  {
    id: '1',
    title: 'Processo de Aprovação de Férias',
    description: 'Template completo para gerenciar solicitações de férias com aprovação em múltiplos níveis e integração com calendário corporativo.',
    category: 'Recursos Humanos',
    rating: 4.8,
    totalRatings: 156,
    downloads: 342,
    author: 'Ana Silva',
    authorRole: 'Analista de RH Sênior',
    thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_1d5d28cec-1765019967380.png",
    thumbnailAlt: 'Pessoa trabalhando em laptop com documentos de férias e calendário sobre mesa de escritório moderna',
    department: 'RH',
    lastUpdated: '15/11/2024',
    tags: ['Férias', 'Aprovação', 'RH', 'Calendário'],
    longDescription: 'Este template oferece um fluxo completo de aprovação de férias, desde a solicitação inicial até a aprovação final. Inclui validações automáticas de saldo de férias, notificações por email, integração com calendário corporativo e relatórios gerenciais. O processo suporta aprovação em múltiplos níveis e permite delegação de aprovadores.',
    features: [
    'Validação automática de saldo de férias disponível',
    'Aprovação em múltiplos níveis (gestor direto e RH)',
    'Notificações automáticas por email em cada etapa',
    'Integração com calendário corporativo',
    'Relatórios de férias programadas por departamento',
    'Histórico completo de solicitações'],

    requirements: [
    'Integração com sistema de RH para consulta de saldo',
    'Configuração de aprovadores por departamento',
    'Acesso ao calendário corporativo'],

    version: '2.1',
    compatibility: ['BPMN 2.0', 'Formulários Dinâmicos', 'Integrações API']
  },
  {
    id: '2',
    title: 'Fluxo de Reembolso de Despesas',
    description: 'Automatize o processo de reembolso de despesas corporativas com validação de políticas e aprovação financeira.',
    category: 'Financeiro',
    rating: 4.6,
    totalRatings: 203,
    downloads: 487,
    author: 'Carlos Santos',
    authorRole: 'Gerente Financeiro',
    thumbnail: "https://images.unsplash.com/photo-1642043175009-5997b3a078d8",
    thumbnailAlt: 'Calculadora, recibos e documentos financeiros organizados sobre mesa com laptop ao fundo',
    department: 'Financeiro',
    lastUpdated: '20/11/2024',
    tags: ['Reembolso', 'Despesas', 'Financeiro', 'Aprovação'],
    longDescription: 'Template robusto para gerenciar reembolsos de despesas corporativas. Inclui upload de comprovantes, validação automática contra políticas da empresa, aprovação em múltiplos níveis e integração com sistema de pagamentos. Suporta diferentes categorias de despesas e moedas.',
    features: [
    'Upload de comprovantes fiscais com OCR',
    'Validação automática contra políticas de despesas',
    'Aprovação em cascata (gestor, financeiro, diretoria)',
    'Conversão automática de moedas estrangeiras',
    'Integração com sistema de pagamentos',
    'Dashboard de despesas por centro de custo'],

    requirements: [
    'Configuração de políticas de despesas por categoria',
    'Integração com sistema de pagamentos',
    'Definição de limites de aprovação por nível'],

    version: '3.0',
    compatibility: ['BPMN 2.0', 'Upload de Arquivos', 'Integrações API', 'OCR']
  },
  {
    id: '3',
    title: 'Onboarding de Novos Colaboradores',
    description: 'Processo estruturado para integração de novos funcionários com checklist completo e acompanhamento de tarefas.',
    category: 'Recursos Humanos',
    rating: 4.9,
    totalRatings: 178,
    downloads: 521,
    author: 'Maria Oliveira',
    authorRole: 'Coordenadora de RH',
    thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_1ef571c99-1764670655764.png",
    thumbnailAlt: 'Grupo de profissionais em reunião de boas-vindas com novos colaboradores em sala moderna',
    department: 'RH',
    lastUpdated: '18/11/2024',
    tags: ['Onboarding', 'RH', 'Integração', 'Treinamento'],
    longDescription: 'Template completo para onboarding de novos colaboradores. Inclui checklist de documentação, agendamento de treinamentos, configuração de acessos, apresentação da equipe e acompanhamento dos primeiros 90 dias. Automatiza notificações para todas as áreas envolvidas.',
    features: [
    'Checklist automatizado de documentação',
    'Agendamento de treinamentos obrigatórios',
    'Solicitação automática de equipamentos e acessos',
    'Apresentação virtual da equipe',
    'Acompanhamento de progresso nos primeiros 90 dias',
    'Pesquisa de satisfação automatizada'],

    requirements: [
    'Integração com sistema de gestão de pessoas',
    'Acesso ao sistema de treinamentos',
    'Configuração de responsáveis por área'],

    version: '2.5',
    compatibility: ['BPMN 2.0', 'Formulários Dinâmicos', 'Agendamento', 'Pesquisas']
  },
  {
    id: '4',
    title: 'Aprovação de Orçamento Anual',
    description: 'Gerencie o processo de aprovação de orçamentos anuais com múltiplas revisões e consolidação de dados.',
    category: 'Financeiro',
    rating: 4.7,
    totalRatings: 89,
    downloads: 234,
    author: 'João Costa',
    authorRole: 'Controller',
    thumbnail: "https://images.unsplash.com/photo-1723095469034-c3cf31e32730",
    thumbnailAlt: 'Gráficos financeiros e planilhas de orçamento exibidos em tela de computador com calculadora ao lado',
    department: 'Financeiro',
    lastUpdated: '22/11/2024',
    tags: ['Orçamento', 'Planejamento', 'Financeiro', 'Aprovação'],
    longDescription: 'Template especializado para o ciclo de aprovação de orçamentos anuais. Permite submissão por centro de custo, revisões em múltiplos níveis, consolidação automática de dados e comparação com anos anteriores. Inclui workflows de ajustes e renegociações.',
    features: [
    'Submissão de orçamento por centro de custo',
    'Revisão e aprovação em múltiplos níveis',
    'Consolidação automática de dados',
    'Comparação com orçamento anterior',
    'Workflow de ajustes e renegociações',
    'Relatórios executivos consolidados'],

    requirements: [
    'Estrutura de centros de custo configurada',
    'Dados históricos de orçamentos anteriores',
    'Definição de aprovadores por nível orçamentário'],

    version: '1.8',
    compatibility: ['BPMN 2.0', 'Formulários Dinâmicos', 'Relatórios', 'Integrações API']
  },
  {
    id: '5',
    title: 'Gestão de Contratos',
    description: 'Controle todo o ciclo de vida de contratos desde a criação até a renovação com alertas automáticos.',
    category: 'Jurídico',
    rating: 4.5,
    totalRatings: 124,
    downloads: 298,
    author: 'Patricia Lima',
    authorRole: 'Advogada Corporativa',
    thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_13aaca457-1764644325350.png",
    thumbnailAlt: 'Documentos de contrato com caneta e selo sobre mesa de escritório com laptop ao fundo',
    department: 'Jurídico',
    lastUpdated: '25/11/2024',
    tags: ['Contratos', 'Jurídico', 'Gestão', 'Renovação'],
    longDescription: 'Template completo para gestão do ciclo de vida de contratos. Inclui criação de minutas, aprovação jurídica, assinatura eletrônica, armazenamento seguro e alertas automáticos de vencimento. Suporta diferentes tipos de contratos e cláusulas personalizadas.',
    features: [
    'Biblioteca de modelos de contratos',
    'Workflow de aprovação jurídica',
    'Integração com assinatura eletrônica',
    'Armazenamento seguro de documentos',
    'Alertas automáticos de vencimento',
    'Histórico de versões e alterações'],

    requirements: [
    'Integração com plataforma de assinatura eletrônica',
    'Configuração de aprovadores jurídicos',
    'Sistema de armazenamento de documentos'],

    version: '2.3',
    compatibility: ['BPMN 2.0', 'Assinatura Eletrônica', 'Gestão Documental', 'Alertas']
  },
  {
    id: '6',
    title: 'Solicitação de Compras',
    description: 'Automatize requisições de compras com aprovação por alçada e integração com fornecedores.',
    category: 'Compras',
    rating: 4.4,
    totalRatings: 167,
    downloads: 412,
    author: 'Roberto Alves',
    authorRole: 'Gerente de Suprimentos',
    thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_191ca20e1-1764675977590.png",
    thumbnailAlt: 'Pessoa analisando pedidos de compra em tablet com caixas de produtos ao fundo em armazém',
    department: 'Compras',
    lastUpdated: '28/11/2024',
    tags: ['Compras', 'Requisição', 'Aprovação', 'Fornecedores'],
    longDescription: 'Template otimizado para processos de compras corporativas. Permite solicitação de itens, cotação com múltiplos fornecedores, aprovação por alçada de valores, geração automática de pedidos e acompanhamento de entregas. Inclui integração com sistema de estoque.',
    features: [
    'Catálogo de produtos e serviços',
    'Cotação automática com fornecedores',
    'Aprovação por alçada de valores',
    'Geração automática de pedidos de compra',
    'Acompanhamento de entregas',
    'Integração com controle de estoque'],

    requirements: [
    'Cadastro de fornecedores atualizado',
    'Definição de alçadas de aprovação',
    'Integração com sistema de estoque'],

    version: '3.2',
    compatibility: ['BPMN 2.0', 'Formulários Dinâmicos', 'Integrações API', 'Notificações']
  },
  {
    id: '7',
    title: 'Avaliação de Desempenho',
    description: 'Processo estruturado de avaliação 360° com metas, competências e plano de desenvolvimento individual.',
    category: 'Recursos Humanos',
    rating: 4.7,
    totalRatings: 142,
    downloads: 376,
    author: 'Ana Silva',
    authorRole: 'Analista de RH Sênior',
    thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_1061e7a0b-1764666205194.png",
    thumbnailAlt: 'Reunião de avaliação de desempenho entre gestor e colaborador com documentos sobre mesa',
    department: 'RH',
    lastUpdated: '02/12/2024',
    tags: ['Avaliação', 'Desempenho', 'RH', '360°'],
    longDescription: 'Template completo para ciclo de avaliação de desempenho. Inclui autoavaliação, avaliação do gestor, feedback 360°, definição de metas, avaliação de competências e criação de plano de desenvolvimento individual. Gera relatórios consolidados para análise de talentos.',
    features: [
    'Autoavaliação estruturada',
    'Avaliação do gestor direto',
    'Feedback 360° (pares, subordinados, clientes)',
    'Definição e acompanhamento de metas',
    'Avaliação de competências comportamentais',
    'Plano de desenvolvimento individual (PDI)'],

    requirements: [
    'Matriz de competências definida',
    'Configuração de avaliadores por colaborador',
    'Integração com sistema de gestão de pessoas'],

    version: '2.7',
    compatibility: ['BPMN 2.0', 'Formulários Dinâmicos', 'Pesquisas', 'Relatórios']
  },
  {
    id: '8',
    title: 'Abertura de Chamados de TI',
    description: 'Sistema de tickets para suporte técnico com priorização automática e SLA configurável.',
    category: 'TI',
    rating: 4.6,
    totalRatings: 198,
    downloads: 543,
    author: 'Carlos Santos',
    authorRole: 'Gerente de TI',
    thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_1d2168e49-1764651381819.png",
    thumbnailAlt: 'Técnico de TI trabalhando em computador com múltiplos monitores mostrando sistemas de suporte',
    department: 'TI',
    lastUpdated: '05/12/2024',
    tags: ['Suporte', 'TI', 'Chamados', 'SLA'],
    longDescription: 'Template especializado para gestão de chamados de suporte técnico. Inclui categorização automática, priorização baseada em impacto e urgência, atribuição inteligente de técnicos, SLA configurável e base de conhecimento integrada. Suporta escalação automática.',
    features: [
    'Categorização automática de chamados',
    'Priorização por impacto e urgência',
    'Atribuição inteligente de técnicos',
    'SLA configurável por categoria',
    'Base de conhecimento integrada',
    'Escalação automática de chamados críticos'],

    requirements: [
    'Cadastro de técnicos e especialidades',
    'Configuração de SLA por categoria',
    'Base de conhecimento populada'],

    version: '4.1',
    compatibility: ['BPMN 2.0', 'Formulários Dinâmicos', 'Base de Conhecimento', 'SLA']
  }];


  const filteredTemplates = mockTemplates.filter((template) => {
    const matchesSearch =
    searchQuery === '' ||
    template.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    template.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
    template.tags.some((tag) => tag.toLowerCase().includes(searchQuery.toLowerCase()));

    const matchesCategory =
    filters.categories.length === 0 || filters.categories.includes(template.category);

    const matchesDepartment =
    filters.departments.length === 0 || filters.departments.includes(template.department);

    const matchesAuthor =
    filters.authors.length === 0 || filters.authors.includes(template.author);

    const matchesRating = template.rating >= filters.minRating;

    const matchesDownloads = template.downloads >= filters.minDownloads;

    return (
      matchesSearch &&
      matchesCategory &&
      matchesDepartment &&
      matchesAuthor &&
      matchesRating &&
      matchesDownloads);

  });

  const sortedTemplates = [...filteredTemplates].sort((a, b) => {
    switch (sortBy) {
      case 'popular':
        return b.downloads - a.downloads;
      case 'recent':
        return new Date(b.lastUpdated.split('/').reverse().join('-')).getTime() -
        new Date(a.lastUpdated.split('/').reverse().join('-')).getTime();
      case 'rating':
        return b.rating - a.rating;
      case 'downloads':
        return b.downloads - a.downloads;
      default:
        return 0;
    }
  });

  const handleUseTemplate = (id: string) => {
    router.push(`/process-creation-wizard?templateId=${id}`);
  };

  const handleViewDetails = (id: string) => {
    const template = mockTemplates.find((t) => t.id === id);
    if (template) {
      setSelectedTemplate(template);
    }
  };

  const handleRate = (id: string, rating: number) => {
    console.log(`Template ${id} rated ${rating} stars`);
  };

  if (!isHydrated) {
    return (
      <div data-component-id="src/app/template-gallery/components/TemplateGalleryInteractive.tsx:373:6" data-component-path="src/app/template-gallery/components/TemplateGalleryInteractive.tsx" data-component-line="373" data-component-file="TemplateGalleryInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex-1%20flex%20items-center%20justify-center%22%7D" className="flex-1 flex items-center justify-center">
        <div data-component-id="src/app/template-gallery/components/TemplateGalleryInteractive.tsx:374:8" data-component-path="src/app/template-gallery/components/TemplateGalleryInteractive.tsx" data-component-line="374" data-component-file="TemplateGalleryInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22animate-pulse%20text-muted-foreground%22%2C%22textContent%22%3A%22Carregando%20galeria...%22%7D" className="animate-pulse text-muted-foreground">Carregando galeria...</div>
      </div>);

  }

  return (
    <div data-component-id="src/app/template-gallery/components/TemplateGalleryInteractive.tsx:380:4" data-component-path="src/app/template-gallery/components/TemplateGalleryInteractive.tsx" data-component-line="380" data-component-file="TemplateGalleryInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex-1%20flex%22%7D" className="flex-1 flex">
      <FilterSidebar data-component-id="src/app/template-gallery/components/TemplateGalleryInteractive.tsx:381:6" data-component-path="src/app/template-gallery/components/TemplateGalleryInteractive.tsx" data-component-line="381" data-component-file="TemplateGalleryInteractive.tsx" data-component-name="FilterSidebar" data-component-content="%7B%22elementName%22%3A%22FilterSidebar%22%7D" filters={filters} onFilterChange={setFilters} />

      <div data-component-id="src/app/template-gallery/components/TemplateGalleryInteractive.tsx:383:6" data-component-path="src/app/template-gallery/components/TemplateGalleryInteractive.tsx" data-component-line="383" data-component-file="TemplateGalleryInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex-1%20overflow-y-auto%22%7D" className="flex-1 overflow-y-auto">
        <div data-component-id="src/app/template-gallery/components/TemplateGalleryInteractive.tsx:384:8" data-component-path="src/app/template-gallery/components/TemplateGalleryInteractive.tsx" data-component-line="384" data-component-file="TemplateGalleryInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22p-6%20space-y-6%22%7D" className="p-6 space-y-6">
          <div data-component-id="src/app/template-gallery/components/TemplateGalleryInteractive.tsx:385:10" data-component-path="src/app/template-gallery/components/TemplateGalleryInteractive.tsx" data-component-line="385" data-component-file="TemplateGalleryInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20flex-col%20lg%3Aflex-row%20lg%3Aitems-center%20justify-between%20gap-4%22%7D" className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
            <div data-component-id="src/app/template-gallery/components/TemplateGalleryInteractive.tsx:386:12" data-component-path="src/app/template-gallery/components/TemplateGalleryInteractive.tsx" data-component-line="386" data-component-file="TemplateGalleryInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex-1%20max-w-2xl%22%7D" className="flex-1 max-w-2xl">
              <div data-component-id="src/app/template-gallery/components/TemplateGalleryInteractive.tsx:387:14" data-component-path="src/app/template-gallery/components/TemplateGalleryInteractive.tsx" data-component-line="387" data-component-file="TemplateGalleryInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22relative%22%7D" className="relative">
                <Icon data-component-id="src/app/template-gallery/components/TemplateGalleryInteractive.tsx:388:16" data-component-path="src/app/template-gallery/components/TemplateGalleryInteractive.tsx" data-component-line="388" data-component-file="TemplateGalleryInteractive.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22MagnifyingGlassIcon%22%2C%22className%22%3A%22absolute%20left-3%20top-1%2F2%20-translate-y-1%2F2%20text-muted-foreground%22%7D"
                  name="MagnifyingGlassIcon"
                  size={20}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />

                <input data-component-id="src/app/template-gallery/components/TemplateGalleryInteractive.tsx:393:16" data-component-path="src/app/template-gallery/components/TemplateGalleryInteractive.tsx" data-component-line="393" data-component-file="TemplateGalleryInteractive.tsx" data-component-name="input" data-component-content="%7B%22elementName%22%3A%22input%22%2C%22type%22%3A%22text%22%2C%22value%22%3A%22%5Bvar%3AsearchQuery%5D%22%2C%22className%22%3A%22w-full%20h-12%20pl-10%20pr-4%20bg-surface%20border%20border-border%20rounded-md%20text-sm%20focus%3Aoutline-none%20focus%3Aring-2%20focus%3Aring-ring%20transition-micro%22%7D"
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Buscar templates por nome, descrição ou tags..."
                  className="w-full h-12 pl-10 pr-4 bg-surface border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring transition-micro" />

              </div>
            </div>

            <div data-component-id="src/app/template-gallery/components/TemplateGalleryInteractive.tsx:403:12" data-component-path="src/app/template-gallery/components/TemplateGalleryInteractive.tsx" data-component-line="403" data-component-file="TemplateGalleryInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20gap-3%22%7D" className="flex items-center gap-3">
              <select data-component-id="src/app/template-gallery/components/TemplateGalleryInteractive.tsx:404:14" data-component-path="src/app/template-gallery/components/TemplateGalleryInteractive.tsx" data-component-line="404" data-component-file="TemplateGalleryInteractive.tsx" data-component-name="select" data-component-content="%7B%22elementName%22%3A%22select%22%2C%22value%22%3A%22%5Bvar%3AsortBy%5D%22%2C%22className%22%3A%22h-10%20px-4%20bg-surface%20border%20border-border%20rounded-md%20text-sm%20focus%3Aoutline-none%20focus%3Aring-2%20focus%3Aring-ring%20transition-micro%22%7D"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as any)}
                className="h-10 px-4 bg-surface border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring transition-micro">

                <option data-component-id="src/app/template-gallery/components/TemplateGalleryInteractive.tsx:409:16" data-component-path="src/app/template-gallery/components/TemplateGalleryInteractive.tsx" data-component-line="409" data-component-file="TemplateGalleryInteractive.tsx" data-component-name="option" data-component-content="%7B%22elementName%22%3A%22option%22%2C%22value%22%3A%22popular%22%2C%22textContent%22%3A%22Mais%20Populares%22%7D" value="popular">Mais Populares</option>
                <option data-component-id="src/app/template-gallery/components/TemplateGalleryInteractive.tsx:410:16" data-component-path="src/app/template-gallery/components/TemplateGalleryInteractive.tsx" data-component-line="410" data-component-file="TemplateGalleryInteractive.tsx" data-component-name="option" data-component-content="%7B%22elementName%22%3A%22option%22%2C%22value%22%3A%22recent%22%2C%22textContent%22%3A%22Mais%20Recentes%22%7D" value="recent">Mais Recentes</option>
                <option data-component-id="src/app/template-gallery/components/TemplateGalleryInteractive.tsx:411:16" data-component-path="src/app/template-gallery/components/TemplateGalleryInteractive.tsx" data-component-line="411" data-component-file="TemplateGalleryInteractive.tsx" data-component-name="option" data-component-content="%7B%22elementName%22%3A%22option%22%2C%22value%22%3A%22rating%22%2C%22textContent%22%3A%22Melhor%20Avaliados%22%7D" value="rating">Melhor Avaliados</option>
                <option data-component-id="src/app/template-gallery/components/TemplateGalleryInteractive.tsx:412:16" data-component-path="src/app/template-gallery/components/TemplateGalleryInteractive.tsx" data-component-line="412" data-component-file="TemplateGalleryInteractive.tsx" data-component-name="option" data-component-content="%7B%22elementName%22%3A%22option%22%2C%22value%22%3A%22downloads%22%2C%22textContent%22%3A%22Mais%20Baixados%22%7D" value="downloads">Mais Baixados</option>
              </select>

              <div data-component-id="src/app/template-gallery/components/TemplateGalleryInteractive.tsx:415:14" data-component-path="src/app/template-gallery/components/TemplateGalleryInteractive.tsx" data-component-line="415" data-component-file="TemplateGalleryInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20gap-1%20bg-muted%20rounded-md%20p-1%22%7D" className="flex items-center gap-1 bg-muted rounded-md p-1">
                <button data-component-id="src/app/template-gallery/components/TemplateGalleryInteractive.tsx:416:16" data-component-path="src/app/template-gallery/components/TemplateGalleryInteractive.tsx" data-component-line="416" data-component-file="TemplateGalleryInteractive.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%7D"
                  onClick={() => setViewMode('grid')}
                  className={`p-2 rounded transition-micro ${
                  viewMode === 'grid' ? 'bg-surface shadow-sm' : 'hover:bg-surface/50'}`
                  }
                  aria-label="Visualização em grade">

                  <Icon data-component-id="src/app/template-gallery/components/TemplateGalleryInteractive.tsx:423:18" data-component-path="src/app/template-gallery/components/TemplateGalleryInteractive.tsx" data-component-line="423" data-component-file="TemplateGalleryInteractive.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22Squares2X2Icon%22%7D"
                    name="Squares2X2Icon"
                    size={20}
                    className={viewMode === 'grid' ? 'text-primary' : 'text-muted-foreground'} />

                </button>
                <button data-component-id="src/app/template-gallery/components/TemplateGalleryInteractive.tsx:429:16" data-component-path="src/app/template-gallery/components/TemplateGalleryInteractive.tsx" data-component-line="429" data-component-file="TemplateGalleryInteractive.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%7D"
                  onClick={() => setViewMode('list')}
                  className={`p-2 rounded transition-micro ${
                  viewMode === 'list' ? 'bg-surface shadow-sm' : 'hover:bg-surface/50'}`
                  }
                  aria-label="Visualização em lista">

                  <Icon data-component-id="src/app/template-gallery/components/TemplateGalleryInteractive.tsx:436:18" data-component-path="src/app/template-gallery/components/TemplateGalleryInteractive.tsx" data-component-line="436" data-component-file="TemplateGalleryInteractive.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22ListBulletIcon%22%7D"
                    name="ListBulletIcon"
                    size={20}
                    className={viewMode === 'list' ? 'text-primary' : 'text-muted-foreground'} />

                </button>
              </div>
            </div>
          </div>

          <div data-component-id="src/app/template-gallery/components/TemplateGalleryInteractive.tsx:446:10" data-component-path="src/app/template-gallery/components/TemplateGalleryInteractive.tsx" data-component-line="446" data-component-file="TemplateGalleryInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20justify-between%22%7D" className="flex items-center justify-between">
            <p data-component-id="src/app/template-gallery/components/TemplateGalleryInteractive.tsx:447:12" data-component-path="src/app/template-gallery/components/TemplateGalleryInteractive.tsx" data-component-line="447" data-component-file="TemplateGalleryInteractive.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-sm%20text-muted-foreground%22%7D" className="text-sm text-muted-foreground">
              {sortedTemplates.length} {sortedTemplates.length === 1 ? 'template encontrado' : 'templates encontrados'}
            </p>
            {(filters.categories.length > 0 ||
            filters.departments.length > 0 ||
            filters.authors.length > 0 ||
            filters.minRating > 0 ||
            filters.minDownloads > 0) &&
            <button data-component-id="src/app/template-gallery/components/TemplateGalleryInteractive.tsx:455:12" data-component-path="src/app/template-gallery/components/TemplateGalleryInteractive.tsx" data-component-line="455" data-component-file="TemplateGalleryInteractive.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22text-sm%20text-accent%20hover%3Atext-accent%2F80%20transition-micro%20flex%20items-center%20gap-2%22%2C%22textContent%22%3A%22Limpar%20Filtros%22%7D"
              onClick={() =>
              setFilters({
                categories: [],
                departments: [],
                authors: [],
                minRating: 0,
                minDownloads: 0
              })
              }
              className="text-sm text-accent hover:text-accent/80 transition-micro flex items-center gap-2">

                <Icon data-component-id="src/app/template-gallery/components/TemplateGalleryInteractive.tsx:467:16" data-component-path="src/app/template-gallery/components/TemplateGalleryInteractive.tsx" data-component-line="467" data-component-file="TemplateGalleryInteractive.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22XMarkIcon%22%7D" name="XMarkIcon" size={16} />
                Limpar Filtros
              </button>
            }
          </div>

          {sortedTemplates.length === 0 ?
          <div data-component-id="src/app/template-gallery/components/TemplateGalleryInteractive.tsx:474:10" data-component-path="src/app/template-gallery/components/TemplateGalleryInteractive.tsx" data-component-line="474" data-component-file="TemplateGalleryInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20flex-col%20items-center%20justify-center%20py-16%20text-center%22%7D" className="flex flex-col items-center justify-center py-16 text-center">
              <Icon data-component-id="src/app/template-gallery/components/TemplateGalleryInteractive.tsx:475:14" data-component-path="src/app/template-gallery/components/TemplateGalleryInteractive.tsx" data-component-line="475" data-component-file="TemplateGalleryInteractive.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22MagnifyingGlassIcon%22%2C%22className%22%3A%22text-muted-foreground%20mb-4%20opacity-50%22%7D" name="MagnifyingGlassIcon" size={64} className="text-muted-foreground mb-4 opacity-50" />
              <h3 data-component-id="src/app/template-gallery/components/TemplateGalleryInteractive.tsx:476:14" data-component-path="src/app/template-gallery/components/TemplateGalleryInteractive.tsx" data-component-line="476" data-component-file="TemplateGalleryInteractive.tsx" data-component-name="h3" data-component-content="%7B%22elementName%22%3A%22h3%22%2C%22className%22%3A%22text-xl%20font-semibold%20text-foreground%20mb-2%22%2C%22textContent%22%3A%22Nenhum%20template%20encontrado%22%7D" className="text-xl font-semibold text-foreground mb-2">
                Nenhum template encontrado
              </h3>
              <p data-component-id="src/app/template-gallery/components/TemplateGalleryInteractive.tsx:479:14" data-component-path="src/app/template-gallery/components/TemplateGalleryInteractive.tsx" data-component-line="479" data-component-file="TemplateGalleryInteractive.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-muted-foreground%20max-w-md%22%2C%22textContent%22%3A%22Tente%20ajustar%20seus%20filtros%20ou%20termos%20de%20busca%20para%20encontrar%20templates%20relevantes.%22%7D" className="text-muted-foreground max-w-md">
                Tente ajustar seus filtros ou termos de busca para encontrar templates relevantes.
              </p>
            </div> :

          <div data-component-id="src/app/template-gallery/components/TemplateGalleryInteractive.tsx:484:10" data-component-path="src/app/template-gallery/components/TemplateGalleryInteractive.tsx" data-component-line="484" data-component-file="TemplateGalleryInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D"
            className={
            viewMode === 'grid' ? 'grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-6' : 'space-y-4'
            }>

              {sortedTemplates.map((template) =>
            <TemplateCard data-component-id="src/app/template-gallery/components/TemplateGalleryInteractive.tsx:490:12" data-component-path="src/app/template-gallery/components/TemplateGalleryInteractive.tsx" data-component-line="490" data-component-file="TemplateGalleryInteractive.tsx" data-component-name="TemplateCard" data-component-content="%7B%22elementName%22%3A%22TemplateCard%22%7D"
              key={template.id}
              template={template}
              onUseTemplate={handleUseTemplate}
              onViewDetails={handleViewDetails}
              onRate={handleRate} />

            )}
            </div>
          }
        </div>
      </div>

      {selectedTemplate &&
      <TemplateDetailsModal data-component-id="src/app/template-gallery/components/TemplateGalleryInteractive.tsx:504:6" data-component-path="src/app/template-gallery/components/TemplateGalleryInteractive.tsx" data-component-line="504" data-component-file="TemplateGalleryInteractive.tsx" data-component-name="TemplateDetailsModal" data-component-content="%7B%22elementName%22%3A%22TemplateDetailsModal%22%7D"
        template={selectedTemplate}
        onClose={() => setSelectedTemplate(null)}
        onUseTemplate={handleUseTemplate} />

      }
    </div>);

};

export default TemplateGalleryInteractive;